import React from 'react';

/**
 * OnboardingProgress - Step indicator for onboarding wizard
 * 
 * Shows visual progress through the onboarding steps with:
 * - Step circles with completion states
 * - Step labels
 * - Progress bar with enhanced visibility
 */
export function OnboardingProgress({ currentStep, totalSteps = 4 }) {
	const steps = [
		{ id: 1, label: 'Client', description: 'Create client' },
		{ id: 2, label: 'Talent', description: 'Add account' },
		{ id: 3, label: 'Platforms', description: 'Select platforms' },
		{ id: 4, label: 'Settings', description: 'Configure monitoring' },
	];

	return (
		<div className="onboarding-progress">
			{/* Progress bar with enhanced visibility */}
			<div className="relative mb-8">
				<div className="w-full bg-input rounded-full h-3 shadow-sm">
					<div
						className="bg-gradient-to-r from-brand-500 to-brand-600 h-3 rounded-full transition-all duration-500 ease-out shadow-sm"
						style={{
							width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%`,
						}}
					/>
				</div>
			</div>

			{/* Step indicators with improved visibility */}
			<div className="flex justify-between items-start">
				{steps.map((step, index) => {
					const isCompleted = step.id < currentStep;
					const isCurrent = step.id === currentStep;
			
					return (
						<div key={step.id} className="flex flex-col items-center max-w-[120px]">
							{/* Step circle with enhanced states */}
							<div
								className={`
									w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold
									transition-all duration-300 ease-in-out border-2
									${isCompleted
										? 'bg-green-500 text-white border-green-500 shadow-lg'
										: isCurrent
											? 'bg-brand-600 text-white border-brand-600 ring-4 ring-brand-200 shadow-lg'
											: 'bg-card text-muted-foreground border-input hover:border-muted-foreground'
									}
								`}
							>
								{isCompleted ? (
									<svg
										className="w-5 h-5"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2.5}
											d="M5 13l4 4L19 7"
										/>
									</svg>
								) : (
									step.id
								)}
							</div>

							{/* Step label with improved contrast */}
							<div className="mt-3 text-center">
								<div
									className={`
										text-sm font-semibold leading-tight
										${isCompleted
											? 'text-green-700'
											: isCurrent
												? 'text-brand-700'
												: 'text-foreground'
										}
									`}
								>
									{step.label}
								</div>
								<div
									className={`
										text-xs mt-1 leading-tight
										${isCompleted || isCurrent
											? 'text-muted-foreground'
											: 'text-muted-foreground'
										}
									`}
								>
									{step.description}
								</div>
							</div>
						</div>
					);
				})}
			</div>

			{/* Remove redundant step counter since it's now at the bottom of the wizard */}
		</div>
	);
} 